import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheck, FiExternalLink, FiTarget } from 'react-icons/fi';
import { API_BASE_URL, getAuthHeaders } from '../../utils/utils';
import './GraduateTargeting.css';

const LEVELS = [
  { value: 'bachelor', label: 'Bachelor / Licence' },
  { value: 'master', label: 'Master' },
  { value: 'professional', label: 'Working professional' },
  { value: 'other', label: 'Other' },
];

const STUDY_CHIPS = [
  'Licence in economics',
  'Computer science',
  'Engineering',
  'Working in consulting',
  'Law / droit',
  'Marketing',
];

const GOAL_CHIPS = [
  { label: 'MBA', fill: 'MBA' },
  { label: 'MiM', fill: 'Master in Management' },
  { label: 'MSc Finance', fill: 'MSc Finance' },
  { label: 'Executive MBA', fill: 'Executive MBA' },
  { label: 'Public policy', fill: 'public policy master' },
  { label: 'Data science', fill: 'data science master' },
  { label: 'Computer science', fill: 'computer science master' },
  { label: 'Consulting', fill: 'consulting career' },
];

const FAMILY_HINTS = [
  { id: 'executive_mba', label: 'Executive MBA', terms: ['executive mba', 'emba', 'gemba', 'mba executif'] },
  { id: 'mba', label: 'MBA', terms: ['mba', 'business school', 'business administration'] },
  { id: 'mim', label: 'Master in Management', terms: ['mim', 'master in management', 'masters in management', 'master en management'] },
  { id: 'msc_finance', label: 'MSc Finance', terms: ['msc finance', 'master in finance', 'masters in finance', 'master en finance', 'mif'] },
];

const FIELD_HINTS = [
  { id: 'public_policy', label: 'public policy', terms: ['public policy', 'mpp', 'mpa', 'politique publique'] },
  { id: 'data_science', label: 'data science', terms: ['data science', 'machine learning', 'analytics'] },
  { id: 'computer_science', label: 'computer science', terms: ['computer science', 'informatique', 'software'] },
  { id: 'finance', label: 'finance', terms: ['finance', 'investment'] },
  { id: 'law', label: 'law', terms: ['law', 'llm', 'droit', 'avocat'] },
  { id: 'engineering', label: 'engineering', terms: ['engineering', 'ingenieur', 'engineer'] },
  { id: 'management', label: 'management', terms: ['management', 'consulting', 'strategy', 'conseil'] },
  { id: 'economics', label: 'economics', terms: ['economics', 'economie'] },
];

const normalizeHintText = (value) =>
  `${value || ''}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const previewIntent = (currentStudy, futureGoal, currentLevel) => {
  const text = normalizeHintText(`${currentStudy} ${futureGoal}`);
  const families = FAMILY_HINTS.filter((item) =>
    item.terms.some((term) => text.includes(term))
  );
  const field = FIELD_HINTS.find((item) => item.terms.some((term) => text.includes(term)));
  if (families.length) {
    return {
      confidence: 'high',
      summary: [...families.map((item) => item.label), field?.label].filter(Boolean).join(' · '),
    };
  }
  if (currentLevel === 'professional' || /\b(work|job|career|consultant|consulting|travail|emploi)\b/.test(text)) {
    return { confidence: 'medium', summary: 'MBA' };
  }
  if (field) {
    return { confidence: 'medium', summary: `Specialized master · ${field.label}` };
  }
  if (!text.trim()) {
    return null;
  }
  return {
    confidence: 'low',
    summary: 'Specialized masters — add MBA, MiM, finance, or a field for a tighter list',
  };
};

const familyLabel = (family) =>
  ({
    executive_mba: 'Executive MBA',
    mba: 'MBA',
    mim: 'MiM',
    msc_finance: 'MSc Finance',
    specialized_master: 'Specialized master',
  }[family] || family);

const sourced = (item) =>
  item?.source_url ? (
    <a href={item.source_url} target="_blank" rel="noreferrer">
      {item.source_name || 'Source'} <FiExternalLink size={12} />
    </a>
  ) : null;

const GraduateTargeting = ({ onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formStep, setFormStep] = useState(1);
  const [currentStudy, setCurrentStudy] = useState('');
  const [currentLevel, setCurrentLevel] = useState('bachelor');
  const [futureGoal, setFutureGoal] = useState('');
  const [match, setMatch] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/targeting`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setExisting(data);
        setCurrentStudy(data.current_study || '');
        setCurrentLevel(data.current_level || 'bachelor');
        setFutureGoal(data.future_goal || '');
        if (data.complete) {
          setSelected((data.targets || []).map((row) => row.program_id));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || window.location.hash !== '#targeting') {
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      document.getElementById('targeting')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [existing]);

  const liveIntent = useMemo(
    () => previewIntent(currentStudy, futureGoal, currentLevel),
    [currentStudy, futureGoal, currentLevel]
  );

  const runMatch = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/targeting/match`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_study: currentStudy,
          current_level: currentLevel,
          future_goal: futureGoal,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Could not match programs');
      }
      setMatch(data);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleProgram = (programId) => {
    setSelected((prev) => {
      if (prev.includes(programId)) {
        return prev.filter((id) => id !== programId);
      }
      if (prev.length >= 3) {
        return [...prev.slice(1), programId];
      }
      return [...prev, programId];
    });
  };

  const saveTargets = async () => {
    if (selected.length !== 3) {
      setError('Choose exactly 3 school × program combinations. The first is your tracking choice.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/targeting`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_study: currentStudy,
          current_level: currentLevel,
          future_goal: futureGoal,
          program_ids: selected,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Could not save targets');
      }
      onComplete?.(data);
      setExisting(data);
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyChip = (value, setter) => {
    setter((prev) => {
      const next = (prev || '').trim();
      if (!next) return value;
      if (normalizeHintText(next).includes(normalizeHintText(value))) return next;
      return `${next}, ${value}`;
    });
  };

  const primary = useMemo(() => {
    if (!match?.programs?.length) return null;
    return match.programs.find((row) => row.id === selected[0]) || null;
  }, [match, selected]);

  const selectedPrograms = useMemo(
    () =>
      selected
        .map((programId) => (match?.programs || []).find((row) => row.id === programId))
        .filter(Boolean),
    [match, selected]
  );

  const selectedCosts = useMemo(() => {
    if (!selectedPrograms.length || !match?.country_costs?.length) return [];
    const countries = new Set(selectedPrograms.map((row) => row.country).filter(Boolean));
    return match.country_costs.filter((row) => countries.has(row.country));
  }, [match, selectedPrograms]);

  const costFamilyLabel =
    match?.country_costs?.[0]?.family_label ||
    familyLabel(match?.cost_family) ||
    familyLabel(selectedPrograms[0]?.family) ||
    'Graduate';

  if (existing?.complete && step === 1) {
    return (
      <section id="targeting" className="gt-card">
        <div className="gt-card__header">
          <div>
            <p className="gt-kicker">
              <FiTarget size={14} /> Graduate targets
            </p>
            <h2>Your 3 school × program picks</h2>
            <p className="gt-sub">
              #1 is the program Home tracks for GRE, English, and deadlines.
            </p>
          </div>
        </div>
        <ol className="gt-targets">
          {(existing.targets || []).map((target) => {
            const program = target.program || {};
            const meta = [
              program.name,
              program.country,
              program.ranking
                ? `#${program.ranking.value} ${program.ranking.source_name || ''}`.trim()
                : null,
            ].filter(Boolean);
            return (
              <li
                key={target.id}
                className={target.priority === 1 ? 'is-tracking' : undefined}
              >
                <span className="gt-targets__rank">{target.priority}</span>
                <div className="gt-targets__body">
                  <div className="gt-targets__title">
                    <strong>{program.school_name}</strong>
                    {target.priority === 1 ? (
                      <span className="gt-badge">Tracking</span>
                    ) : null}
                  </div>
                  {meta.length ? (
                    <span className="gt-targets__meta">{meta.join(' · ')}</span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
        <div className="gt-actions">
          <button
            type="button"
            className="gt-ghost"
            onClick={() => {
              setFormStep(1);
              setStep(2);
            }}
          >
            Update picks
          </button>
          <button
            type="button"
            className="gt-primary"
            onClick={() => navigate('/applications')}
          >
            Open Application Package
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="targeting" className="gt-card">
      <div className="gt-card__intro">
        <p className="gt-kicker">
          <FiTarget size={14} /> Graduate targeting
        </p>
        <h2>Find schools that fit your next step</h2>
      </div>

      {step === 1 || step === 2 ? (
        <div className="gt-form">
          <ol className="gt-stepper" aria-label="Targeting steps">
            <li className={formStep === 1 ? 'is-active' : 'is-done'}>1. Now</li>
            <li className={formStep === 2 ? 'is-active' : formStep > 2 ? 'is-done' : ''}>
              2. Next
            </li>
            <li>3. Schools</li>
          </ol>

          {formStep === 1 ? (
            <>
              <label>
                What are you studying or doing now?
                <input
                  value={currentStudy}
                  onChange={(event) => setCurrentStudy(event.target.value)}
                  placeholder="Licence in economics, computer science, consulting…"
                />
              </label>
              <div className="gt-chips" aria-label="Current study suggestions">
                {STUDY_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    className={`gt-chip ${
                      normalizeHintText(currentStudy).includes(normalizeHintText(chip))
                        ? 'is-active'
                        : ''
                    }`}
                    onClick={() => applyChip(chip, setCurrentStudy)}
                  >
                    {chip}
                  </button>
                ))}
              </div>
              <label>
                Current level
                <select value={currentLevel} onChange={(event) => setCurrentLevel(event.target.value)}>
                  {LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="gt-primary"
                disabled={!currentStudy.trim()}
                onClick={() => setFormStep(2)}
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <label>
                What do you want to become, or keep studying?
                <textarea
                  value={futureGoal}
                  onChange={(event) => setFutureGoal(event.target.value)}
                  placeholder="MBA for consulting, MiM, MSc Finance, public policy…"
                />
              </label>
              <div className="gt-chips" aria-label="Goal suggestions">
                {GOAL_CHIPS.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    className={`gt-chip ${
                      normalizeHintText(futureGoal).includes(normalizeHintText(chip.fill))
                        ? 'is-active'
                        : ''
                    }`}
                    onClick={() => applyChip(chip.fill, setFutureGoal)}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              {liveIntent ? (
                <div className={`gt-understood is-${liveIntent.confidence}`}>
                  <span>We’ll look for</span>
                  <strong>{liveIntent.summary}</strong>
                </div>
              ) : null}
              <div className="gt-actions">
                <button type="button" className="gt-ghost" onClick={() => setFormStep(1)}>
                  Back
                </button>
                <button
                  type="button"
                  className="gt-primary"
                  disabled={loading || !currentStudy.trim() || !futureGoal.trim()}
                  onClick={runMatch}
                >
                  {loading ? 'Matching…' : 'Show matching programs'}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {step >= 3 && match ? (
        <div className="gt-results">
          <button type="button" className="gt-back" onClick={() => {
            setFormStep(2);
            setStep(2);
          }}>
            <FiArrowLeft size={14} /> Edit answers
          </button>
          <div className={`gt-understood is-${match.intent?.confidence || 'medium'}`}>
            <span>We understood</span>
            <strong>
              {match.intent?.summary ||
                `${(match.intent?.families || []).map(familyLabel).join(', ')}${
                  match.intent?.field ? ` · ${match.intent.field}` : ''
                }`}
            </strong>
            {match.ranking_source ? (
              <em>Ranked with {String(match.ranking_source).toUpperCase()}</em>
            ) : null}
          </div>
          {match.intent?.confidence === 'low' ? (
            <p className="gt-note">
              We did not recognize a specific program type, so this is a broader specialized-master
              list. Add MBA, MiM, finance, or a field name and search again.
            </p>
          ) : null}
          {match.unranked_included ? (
            <p className="gt-note">Some Liwaza schools are listed without a rank for this source yet.</p>
          ) : null}

          <div className="gt-results__header">
            <h3>Pick 3 school × program targets</h3>
            <span className="gt-count">{selected.length}/3 selected</span>
          </div>
          <p className="gt-note">First selected is #1 for GRE / English / deadline tracking.</p>

          {selectedPrograms.length ? (
            <ol className="gt-selected-strip">
              {selectedPrograms.map((program, index) => (
                <li key={program.id}>
                  <span className="gt-badge">#{index + 1}</span>
                  <div>
                    <strong>{program.school_name}</strong>
                    <span>
                      {program.name}
                      {program.ranking ? ` · #${program.ranking.value}` : ''}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="gt-note">Tap a school to pin #1, #2, and #3. Costs and the typical dossier appear here.</p>
          )}

          {selectedCosts.length || (selected.length && primary?.requirements?.length) ? (
            <div className="gt-insight">
              {selectedCosts.length ? (
                <div className="gt-costs">
                  <h3>
                    {costFamilyLabel} costs for your picks
                  </h3>
                  <p className="gt-note">
                    Typical full-program {costFamilyLabel} tuition, not undergraduate fees.
                  </p>
                  <ul>
                    {selectedCosts.map((row) => (
                      <li key={row.country}>
                        <strong>{row.country}</strong>: tuition {row.tuition_avg.value}{' '}
                        {row.currency} {sourced(row.tuition_avg)} · living {row.living_avg.value}{' '}
                        {row.currency} {sourced(row.living_avg)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {primary?.requirements?.length ? (
                <div className="gt-reqs">
                  <h3>
                    Typical dossier for {primary.school_name} {primary.name}
                  </h3>
                  <ul>
                    {primary.requirements.map((req) => (
                      <li key={`${req.kind}-${req.label}`}>
                        <strong>{req.label}:</strong> {req.value} {sourced(req)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="gt-program-list">
            {(match.programs || []).map((program) => {
              const index = selected.indexOf(program.id);
              return (
                <button
                  type="button"
                  key={program.id}
                  className={`gt-program ${index >= 0 ? 'is-selected' : ''}`}
                  onClick={() => toggleProgram(program.id)}
                >
                  <div>
                    <strong>
                      {program.school_name}
                    </strong>
                    <span className="gt-program__name">{program.name}</span>
                    <span>
                      {program.country}
                      {program.ranking
                        ? ` · #${program.ranking.value} ${program.ranking.source_name}`
                        : ' · Unranked in this source'}
                    </span>
                    {program.ranking ? sourced(program.ranking) : null}
                  </div>
                  <div className="gt-program__meta">
                    <span className="gt-tag">{familyLabel(program.family)}</span>
                    {index >= 0 ? (
                      <span className="gt-badge">
                        <FiCheck size={12} /> #{index + 1}
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>

          <button type="button" className="gt-primary" disabled={loading} onClick={saveTargets}>
            {loading ? 'Saving…' : 'Save 3 targets'}
          </button>
        </div>
      ) : null}

      {step === 4 ? (
        <p className="gt-done">
          Targets saved. Home now tracks your first choice.{' '}
          <button type="button" className="gt-link" onClick={() => navigate('/applications')}>
            Open Application Package
          </button>
        </p>
      ) : null}

      {error ? <p className="gt-error">{error}</p> : null}
    </section>
  );
};

export default GraduateTargeting;
